import * as React from 'react';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';
import ProductBasicPrice from '../../shop-ui/components/product-item/ProductBasicPrice';
import FormattedCurrency from '../../react/common/components/FormattedCurrency';
import { currencyFormat, louisUrl } from 'PyzShopUi/scripts/react/common/lib';
import FootnoteService from 'PyzShopUi/scripts/shop-ui/services/FootnoteService';
import {EFootnoteNamespace} from 'PyzShopUi/scripts/shop-ui/constants';
import MiddlewareEventBus from 'PyzShopUi/scripts/react/common/lib/MiddlewareEventBus';
import {FootnoteEvents} from 'PyzShopUi/scripts/shop-ui/events/footnoteEvents';
import {FootnoteValues} from 'PyzShopUi/scripts/footnote/constants';

export interface IOrderContainerProps {
    amount: number;
    originalAmount: number;
    basicPrice?: any;
    currency: string;
    batteryDeposit: boolean;
    batteryDepositPrice: number;
    available?: boolean;
    isNew?: boolean;
}

export const OrderContainer: React.FunctionComponent<IOrderContainerProps> = props => {
    const { amount, originalAmount, basicPrice, currency, batteryDeposit, batteryDepositPrice, available, isNew } = props;
    const footnoteService = FootnoteService.getInstance();
    const isPriceReduced = originalAmount && originalAmount > amount;
    const { translate } = useTranslationHook();

    let batteryDepositPriceText: string = '';

    if (batteryDeposit && batteryDepositPrice !== null) {
        batteryDepositPriceText = currencyFormat(batteryDepositPrice / 100, currency);

        footnoteService.addFootnote(FootnoteValues.DEPOSIT, EFootnoteNamespace.PDP_ARTICLE_PRICE);
        MiddlewareEventBus.getInstance().publish({ type: FootnoteEvents.FOOTNOTE_UPDATED, payload: {} });
    }

    const addressCountriesHTMLElement = document.querySelector<HTMLDivElement>('[data-shipping-countries]');
    const shippingCountries: string[] = JSON.parse(addressCountriesHTMLElement.dataset.shippingCountries);
    const shippingCountriesTags = shippingCountries.map((c) => <meta itemProp="addressCountry" content={c} key={c} />);

    return (
        <div className="article-price">
            {
                isPriceReduced ?
                    <div className="heading4">
                        {translate('RRP', { ns: 'article-detail' })}

                        <span className="discount-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                        &nbsp;
                            <span itemProp="price" content={originalAmount / 100} className="price">
                                <meta itemProp="priceCurrency" content={currency} />
                                <FormattedCurrency
                                    price={originalAmount / 100} currency={currency}
                                    footnoteNumber={footnoteService.getFootnoteNumber(FootnoteValues.UVP, EFootnoteNamespace.PDP_ARTICLE_PRICE)} />
                            </span>
                            <sup>

                            </sup>
                        </span>
                    </div>
                    : undefined
            }
            <div className={`price-from${isPriceReduced ? " highlight" : ""}`} itemProp="offers" itemScope itemType="https://schema.org/Offer">
                <span itemProp="price" content={amount / 100} className="heading1">
                    <meta itemProp="priceCurrency" content={currency} />
                    <FormattedCurrency price={amount / 100} currency={currency} />
                </span>

                { available && <link itemProp="availability" href="https://schema.org/InStock" /> }
                { isNew && <meta itemProp="itemCondition" itemType="https://schema.org/NewCondition" />}

                <div itemProp="shippingDetails" itemType="https://schema.org/OfferShippingDetails" itemScope>
                    <div itemProp="shippingDestination" itemType="https://schema.org/DefinedRegion" itemScope>
                        { shippingCountriesTags }
                    </div>
                </div>
            </div>
            {
                basicPrice ?
                    <div className="base-price">
                        <ProductBasicPrice
                            displayLabel
                            currency={currency}
                            unitName={basicPrice.unit_name}
                            unitAmount={basicPrice.unit_amount}
                            unitValue={basicPrice.unit_value} />
                    </div>
                    : undefined
            }

            {batteryDeposit && batteryDepositPrice !== null ?
                <div>
                    <span>
                        &nbsp;<span dangerouslySetInnerHTML={{
                            __html: translate('plus {{value}} deposit', {
                                ns: 'article-detail', replace: {
                                    footnote: footnoteService.getFootnoteNumber(FootnoteValues.GENERAL_VAT, EFootnoteNamespace.PDP_ARTICLE_PRICE),
                                    link: louisUrl('/agb#II-2'), value: batteryDepositPriceText
                                }
                            })
                        }} />
                        <sup>
                            {footnoteService.getFootnoteNumber(FootnoteValues.DEPOSIT, EFootnoteNamespace.PDP_ARTICLE_PRICE)}
                        </sup>
                    </span>
                </div>
                : undefined}
        </div>
    );
};
